import { DotMatrix } from '@clickguard/design-system'
import { useEffect, useState } from 'react'
import { GDialog } from 'components/basic-blocks/g-dialog'
import { useUiStore } from 'store'
import { ProductAccounts } from 'services/user/product-accounts'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { RiCloseLine } from 'react-icons/ri'
import { FiPhoneCall } from 'react-icons/fi'
import moment from 'moment'
import { useAuth0 } from '@auth0/auth0-react'
import {
	setPendingTrialExtension,
	clearPendingTrialExtension
} from 'utils/trial-extension-storage'

type ModalStep =
	| 'loading'
	| 'eligible'
	| 'not_eligible'
	| 'wrong_account'
	| 'extending'
	| 'success_standard'
	| 'success_icp'

const CALENDLY_CSM_CALL_URL = 'https://meetings.hubspot.com/nikola-pejic/account-performance-review'
const ICP_SPEND_THRESHOLD = 10000

export function TrialExtensionDeeplinkModal() {
	const {
		trialExtensionDeeplinkOpen,
		trialExtensionAccountId,
		trialExtensionWrongAccount,
		setTrialExtensionDeeplink
	} = useUiStore()

	const { logout } = useAuth0()
	const trackEvent = useTrackPostHogEvent()

	const [step, setStep] = useState<ModalStep>('loading')
	const [newEndDate, setNewEndDate] = useState<string | null>(null)
	const [segmentationAdSpend, setSegmentationAdSpend] = useState<number>(0)
	const [errorReason, setErrorReason] = useState<string>(
		'Trial extension is no longer valid for this account.'
	)

	const handleClose = () => {
		setTrialExtensionDeeplink(false, null, false)
		setStep('loading')
		setNewEndDate(null)
		setSegmentationAdSpend(0)
	}

	const handleSwitchAccount = () => {
		logout({
			returnTo: `${window.location.origin}/?trial_extension=true&account_id=${trialExtensionAccountId}`
		})
	}

	useEffect(() => {
		if (!trialExtensionDeeplinkOpen || !trialExtensionAccountId) return

		// If wrong account is logged in, show the wrong_account step immediately
		if (trialExtensionWrongAccount) {
			setStep('wrong_account')
			return
		}

		const checkEligibility = async () => {
			setStep('loading')
			try {
				const result =
					await ProductAccounts.checkDeeplinkTrialExtensionEligibility(
						trialExtensionAccountId
					)
				if (result.eligible) {
					setNewEndDate(result.new_end_date)
					setSegmentationAdSpend(result.segmentation_ad_spend || 0)
					setStep('eligible')
					trackEvent(
						PostHogEvent.VIEW_POP_UP,
						PostHogPath.EMAIL_DEEPLINK_TRIAL_EXTENSION
					)
				} else {
					setErrorReason(
						result.reason ||
							'Trial extension is no longer valid for this account.'
					)
					setStep('not_eligible')
				}
			} catch {
				setStep('not_eligible')
			}
		}

		checkEligibility()
	}, [trialExtensionDeeplinkOpen, trialExtensionAccountId, trialExtensionWrongAccount])

	const handleExtend = async () => {
		if (!trialExtensionAccountId) return
		setStep('extending')
		try {
			const result = await ProductAccounts.acceptDeeplinkTrialExtension(
				trialExtensionAccountId
			)
			if (result.success) {
				setNewEndDate(result.new_end_date)
				trackEvent(
					PostHogEvent.ACCEPTED,
					PostHogPath.EMAIL_DEEPLINK_TRIAL_EXTENSION,
					{ extended_by: 7 }
				)
				if (segmentationAdSpend >= ICP_SPEND_THRESHOLD) {
					setStep('success_icp')
				} else {
					setStep('success_standard')
				}
			} else {
				setStep('not_eligible')
			}
		} catch {
			setStep('not_eligible')
		}
	}

	const handleScheduleCSM = () => {
		trackEvent(
			PostHogEvent.SCHEDULE_WITH_CSM,
			PostHogPath.EMAIL_DEEPLINK_TRIAL_EXTENSION
		)
		window.open(CALENDLY_CSM_CALL_URL, '_blank')
	}

	const formattedNewDate = newEndDate
		? moment(newEndDate).format('MMMM D, YYYY')
		: ''

	if (!trialExtensionDeeplinkOpen) return null

	return (
		<GDialog
			open={trialExtensionDeeplinkOpen}
			onClose={handleClose}
			transparentBackground={true}
		>
			<div className="flex flex-row align-items-center justify-center h-full">
				{step === 'loading' && <LoadingStep />}
				{step === 'wrong_account' && (
					<WrongAccountStep
						onClose={handleClose}
						onSwitchAccount={handleSwitchAccount}
					/>
				)}
				{step === 'eligible' && (
					<EligibleStep
						onClose={handleClose}
						onExtend={handleExtend}
						formattedNewDate={formattedNewDate}
					/>
				)}
				{step === 'not_eligible' && (
					<NotEligibleStep onClose={handleClose} reason={errorReason} />
				)}
				{step === 'extending' && <ExtendingStep />}
				{step === 'success_standard' && (
					<SuccessStandardStep
						onClose={handleClose}
						formattedNewDate={formattedNewDate}
					/>
				)}
				{step === 'success_icp' && (
					<SuccessICPStep
						onClose={handleClose}
						onScheduleCSM={handleScheduleCSM}
						formattedNewDate={formattedNewDate}
					/>
				)}
			</div>
		</GDialog>
	)
}

// --- Step Components ---

function LoadingStep() {
	return (
		<div className="flex flex-col w-full max-w-[500px] rounded-[24px] overflow-hidden px-[20px] py-[36px] items-center gap-[16px] bg-white shadow-[0_10px_40px_rgba(0,0,0,0.15)]">
			<DotMatrix />
			<p className="font-grotesk text-[16px] text-t-google-tooltip text-center">
				Checking eligibility...
			</p>
		</div>
	)
}

function CloseButton({ onClose }: { onClose: () => void }) {
	return (
		<button
			type="button"
			onClick={onClose}
			className="absolute top-6 right-6 z-10 text-gray-400 hover:text-gray-500 transition-colors"
			aria-label="Close"
		>
			<RiCloseLine className="w-[20px] h-[20px] aspect-square flex-shrink-0" />
		</button>
	)
}

function EligibleStep({
	onClose,
	onExtend,
	formattedNewDate
}: {
	onClose: () => void
	onExtend: () => void
	formattedNewDate: string
}) {
	return (
		<div className="flex flex-col w-full max-w-[500px] rounded-[24px] overflow-hidden px-[20px] py-[36px] items-start gap-[16px] self-stretch relative z-10 shadow-[0_10px_40px_rgba(0,0,0,0.15)] bg-white">
			<CloseButton onClose={onClose} />
			<div className="flex flex-col gap-[16px] items-center justify-center w-full pt-[24px] font-grotesk">
				<div className="flex flex-col items-center justify-center w-full">
					<h2 className="m-0 self-stretch text-center w-full font-grotesk text-[24px] font-medium leading-[30px] text-t-email-blue">
						Get more time to set up and <br /> experience ClickGuard's protection
					</h2>
				</div>
				<p className="m-0 self-stretch w-full font-grotesk text-[16px] font-normal leading-[22px] text-t-default text-center">
					Click the button below to <br /> 
					extend your free trial for another 7 days.
				</p>
			</div>
			<div className="w-full border-t border-t-traffic flex flex-col gap-[16px] items-center justify-center pt-[24px]">
				<button
					type="button"
					onClick={onExtend}
					className="flex h-[40px] px-[14px] py-[10px] justify-center items-center gap-[4px] rounded-full border border-[#BAE100] bg-[#EEFF9D] font-grotesk text-[14px] font-medium leading-[18px] text-on-lemon shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)] hover:opacity-90 transition-opacity"
				>
					Extend free trial
				</button>
				{formattedNewDate && (
					<p className="m-0 font-grotesk text-[12px] leading-[22px] text-t-google-tooltip text-center">
						Your free trial will be extended until {formattedNewDate}
					</p>
				)}
			</div>
		</div>
	)
}

function WrongAccountStep({
	onClose,
	onSwitchAccount
}: {
	onClose: () => void
	onSwitchAccount: () => void
}) {
	return (
		<div className="flex flex-col w-full max-w-[500px] rounded-[24px] overflow-hidden px-[20px] py-[36px] items-start gap-[16px] self-stretch relative z-10 shadow-[0_10px_40px_rgba(0,0,0,0.15)] bg-white">
			<CloseButton onClose={onClose} />
			<div className="flex flex-col gap-[16px] items-center w-full pt-[24px] font-grotesk">
				<h2 className="m-0 text-center w-full font-grotesk text-[24px] font-medium leading-[30px] text-t-email-blue">
					You're signed in with a different account
				</h2>
				<p className="m-0 w-full text-center font-grotesk text-[16px] font-normal leading-[22px] text-t-google-tooltip">
					To extend your trial, log out, click the link in the email again and sign in with the correct account.
				</p>
			</div>
			<div className="w-full border-t border-t-traffic flex flex-col gap-[12px] items-center justify-center pt-[24px]">
				<button
					type="button"
					onClick={onSwitchAccount}
					className="flex h-[40px] px-[14px] py-[10px] justify-center items-center gap-[4px] rounded-full border border-[#BAE100] bg-[#EEFF9D] font-grotesk text-[14px] font-medium leading-[18px] text-on-lemon shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)] hover:opacity-90 transition-opacity"
				>
					Log Out
				</button>

			</div>
		</div>
	)
}

function NotEligibleStep({
	onClose,
	reason
}: {
	onClose: () => void
	reason: string
}) {
	return (
		<div className="flex flex-col w-full max-w-[500px] rounded-[24px] overflow-hidden px-[20px] py-[36px] items-start gap-[16px] self-stretch relative z-10 shadow-[0_10px_40px_rgba(0,0,0,0.15)] bg-white">
			<CloseButton onClose={onClose} />
			<div className="flex flex-col gap-[16px] items-center w-full pt-[24px] font-grotesk">
				<h2 className="m-0 text-center w-full font-grotesk text-[24px] font-medium leading-[30px] text-t-email-blue">
					Trial Extension Unavailable
				</h2>
				<p className="m-0 w-full text-center font-grotesk text-[16px] font-normal leading-[22px] text-t-google-tooltip">
					{reason}
				</p>
				<button
					type="button"
					onClick={onClose}
					className="mt-4 flex h-[40px] px-[14px] py-[10px] justify-center items-center gap-[4px] rounded-full border border-[#BAE100] bg-[#EEFF9D] font-grotesk text-[14px] font-medium leading-[18px] text-on-lemon shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)] hover:opacity-90 transition-opacity"
				>
					Go to Dashboard
				</button>
			</div>
		</div>
	)
}

function ExtendingStep() {
	return (
		<div className="flex flex-col w-full max-w-[500px] rounded-[24px] overflow-hidden px-[20px] py-[36px] items-center gap-[16px] bg-white shadow-[0_10px_40px_rgba(0,0,0,0.15)]">
			<DotMatrix />
			<p className="font-grotesk text-[16px] text-t-google-tooltip text-center">
				Extending your trial...
			</p>
		</div>
	)
}

function SuccessStandardStep({
	onClose,
	formattedNewDate
}: {
	onClose: () => void
	formattedNewDate: string
}) {
	return (
		<div className="flex flex-col w-full max-w-[500px] rounded-[24px] overflow-hidden px-[20px] py-[36px] items-start gap-[16px] self-stretch relative z-10 shadow-[0_10px_40px_rgba(0,0,0,0.15)] bg-white">
			<CloseButton onClose={onClose} />
			<div className="flex flex-col items-start w-full pt-[24px] font-grotesk">
				<h2 className="m-0 self-stretch text-center w-full font-grotesk text-[24px] font-medium leading-[30px] text-t-email-blue">
					🎉 Your trial was extended until <br /> {formattedNewDate}
				</h2>
			</div>
			<div className="w-full flex justify-center pt-[9px]">
				<button
					type="button"
					onClick={onClose}
					className="flex w-[240px] px-[12px] py-[8px] justify-center items-center gap-[4px] rounded-full border border-[#BAE100] bg-[#EEFF9D] font-grotesk text-[14px] font-normal leading-[15px] text-on-lemon shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)] hover:opacity-90 transition-opacity"
				>
					Go to Dashboard
				</button>
			</div>
		</div>
	)
}

function SuccessICPStep({
	onClose,
	onScheduleCSM,
	formattedNewDate
}: {
	onClose: () => void
	onScheduleCSM: () => void
	formattedNewDate: string
}) {
	return (
		<div className="flex flex-col w-full max-w-[500px] rounded-[24px] overflow-hidden px-[20px] py-[36px] items-start gap-[16px] self-stretch relative z-10 shadow-[0_10px_40px_rgba(0,0,0,0.15)] bg-white">
			<CloseButton onClose={onClose} />
			<div className="flex flex-col gap-[16px] items-start w-full pt-[24px] font-grotesk">
				<div className="flex flex-col items-start w-full">
					<h2 className="m-0 self-stretch text-center w-full font-grotesk text-[24px] font-medium leading-[30px] text-t-email-blue">
						🎉 Your trial was extended until <br /> {formattedNewDate}
					</h2>
				</div>
				<p className="m-0 self-stretch w-full font-grotesk text-[16px] font-normal leading-[22px] text-t-google-tooltip text-center">
					Need help to setup your account? Your dedicated Customer <br /> 
					Support Manager can guide you through the process and <br />
					ensure our system is protecting your budget.
				</p>
			</div>
			<div className="w-full flex justify-center pt-[9px]">
				<button
					type="button"
					onClick={onScheduleCSM}
					className="flex w-[240px] px-[12px] py-[8px] items-center justify-center gap-[4px] rounded-full border border-[#BAE100] bg-[#EEFF9D] font-grotesk text-[14px] font-normal leading-[15px] text-on-lemon shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)] hover:opacity-90 transition-opacity"
				>
					<FiPhoneCall className="w-5 h-5 flex-shrink-0" />
					<span className="underline">
						Book 1:1 call with my CSM
					</span>
				</button>
			</div>
		</div>
	)
}
